import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui'
import type { ChainRunLinkStatus, ChainRunStatus, WorkflowRunStatus, WorkflowPhaseStatus } from '@/types'
import { cn } from '@/lib/utils'
import { CheckCircle2, AlertCircle, AlertTriangle, Clock, Loader2, Play, XCircle, SkipForward, MessageSquare } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

const statusConfig: Record<string, { label: string; className: string; iconClassName: string; Icon: LucideIcon }> = {
  Pending: { label: 'Pending', className: 'bg-neutral text-neutral-foreground border-neutral-foreground', iconClassName: 'text-neutral-foreground', Icon: Clock },
  Running: { label: 'Running', className: 'bg-info text-info-foreground border-info-foreground', iconClassName: 'text-info-foreground', Icon: Loader2 },
  Completed: { label: 'Completed', className: 'bg-success text-success-foreground border-success-foreground', iconClassName: 'text-success-foreground', Icon: CheckCircle2 },
  Failed: { label: 'Failed', className: 'bg-danger text-danger-foreground border border-danger-foreground', iconClassName: 'text-danger-foreground', Icon: AlertCircle },
  Cancelled: { label: 'Cancelled', className: 'bg-cancelled text-cancelled-foreground border-cancelled-foreground', iconClassName: 'text-cancelled-foreground', Icon: XCircle },
  AwaitingApproval: { label: 'Awaiting Approval', className: 'bg-warning text-warning-foreground border-warning-foreground', iconClassName: 'text-warning-foreground', Icon: AlertTriangle },
  AwaitingUserInput: { label: 'Awaiting Input', className: 'bg-warning text-warning-foreground border-warning-foreground', iconClassName: 'text-warning-foreground', Icon: MessageSquare },
  AwaitingInputs: { label: 'Awaiting Inputs', className: 'bg-warning text-warning-foreground border-warning-foreground', iconClassName: 'text-warning-foreground', Icon: MessageSquare },
  AwaitingManualStart: { label: 'Awaiting Manual Start', className: 'bg-warning text-warning-foreground border-warning-foreground', iconClassName: 'text-warning-foreground', Icon: Play },
  Triggered: { label: 'Running', className: 'bg-info text-info-foreground border-info-foreground', iconClassName: 'text-info-foreground', Icon: Loader2 },
  Skipped: { label: 'Skipped', className: 'bg-neutral text-neutral-foreground border-neutral-foreground', iconClassName: 'text-neutral-foreground', Icon: SkipForward },
}

const fallbackConfig = { label: 'Unknown', className: 'bg-neutral text-neutral-foreground border-neutral-foreground', iconClassName: 'text-neutral-foreground', Icon: Clock }

export function WorkflowStatusBadge({ status, iconOnly = false, onPrimary = false, className }: { status: WorkflowRunStatus | WorkflowPhaseStatus | ChainRunStatus | ChainRunLinkStatus; iconOnly?: boolean; onPrimary?: boolean; className?: string }) {
  const config = statusConfig[status] ?? fallbackConfig
  const Icon = config.Icon

  if (iconOnly){
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              role="img"
              aria-label={config.label}
              className={cn(
                'inline-flex h-5 w-5 items-center justify-center rounded-full',
                onPrimary ? 'text-primary-foreground' : config.iconClassName,
                className
              )}
            >
              <Icon
                className={cn('h-3.5 w-3.5', config.Icon === Loader2 && 'animate-spin')}
                data-testid="workflow-status-icon"
              />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <span className="text-sm font-medium truncate">{config.label}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <Badge variant="outline" className={cn(config.className, 'gap-1', className)} data-testid="workflow-status-badge">
      <Icon className={cn('h-3.5 w-3.5', config.Icon === Loader2 && 'animate-spin')} />
      {config.label}
    </Badge>
  )
}
